/**
 * AIF-05 — Defect Catalogue → AI Mapping tab (reworked).
 *
 * The map is no longer a free-text `ai_class_name → type-entry` lookup; it is a
 * full **defect-creation template** keyed by the authoritative `class_uuid`
 * (`ai_finding_model_classes`, AIF-01). All mappers (one per AI model) live in
 * the SAME version-scoped map (class_uuid keying prevents collisions); they
 * save / freeze / branch-copy together.
 *
 * Layout (mockup `.vibe/23 §11.4`):
 *  - a **model switcher** dropdown (active models) with a coverage indicator
 *    (n/N classes mapped), an all|mapped|unmapped filter, and a "Σ all models"
 *    total across the whole map;
 *  - a table of EVERY class of the selected model (`#index · class_name`,
 *    display name) — mapped + unmapped — selectable into a row editor;
 *  - a **row editor**: Defect type via a TREE picker (full breadcrumb path,
 *    leaves only), Location via a tree picker (optional + clearable, soft-warn
 *    on invalid pairs), Severity per class model (inherited / override / reset),
 *    Note (inherited default_description / override / reset).
 *
 * Read-back: the existing map loads via the NEW GET `…/ai-map` on mount (fixes
 * the baseline gap). A class can be mapped only once (PK). Published versions
 * render the whole tab read-only. Save = whole-map PUT (only rows with a
 * typeEntryId are sent). Per-row 422 validation errors are surfaced inline.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type {
  AiClassMapping,
  AiFindingModelClass,
  AiMapValidationIssue,
  DefectCatalogueEntryV2,
  DefectClassModel,
  GetDefectCatalogueV2Response,
} from "@aeriosiq/types";
import { useAiMapQuery, usePutAiMapMutation } from "./taxonomyQueries.js";
import {
  useAiModelsQuery,
  useAiModelQuery,
} from "../ai-models/aiModelQueries.js";
import { locText } from "./locText.js";
import { TreePicker } from "./TreePicker.js";
import { allocationSetsForLocation } from "./allocationResolve.js";
import {
  breadcrumbText,
  buildClassRows,
  classDisplayName,
  classLabel,
  coverage,
  entryBreadcrumb,
  extractAiMapIssues,
  indexEntries,
  inheritedSeverity,
  type CoverageFilter,
} from "./aiMapModel.js";
import {
  InfoIcon,
  PencilIcon,
  RotateCcwIcon,
  TriangleAlertIcon,
} from "@aeriosiq/ui/icons";

export interface AiMappingTabProps {
  catalogueId: string;
  versionId: string;
  structure: GetDefectCatalogueV2Response;
  /** The catalogue's class model(s) — drives severity inherit/override. */
  classModel?: DefectClassModel | null | undefined;
  editable: boolean;
}

export function AiMappingTab({
  catalogueId,
  versionId,
  structure,
  classModel,
  editable,
}: AiMappingTabProps): ReactElement {
  const modelsQuery = useAiModelsQuery();
  const aiMapQuery = useAiMapQuery(catalogueId, versionId);
  const put = usePutAiMapMutation(catalogueId, versionId);

  // Active models for the switcher.
  const models = useMemo(
    () => (modelsQuery.data?.items ?? []).filter((m) => m.isActive),
    [modelsQuery.data],
  );
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  useEffect(() => {
    const first = models[0];
    if (selectedModelId === null && first) {
      setSelectedModelId(first.modelId);
    }
  }, [models, selectedModelId]);
  const modelDetailQuery = useAiModelQuery(selectedModelId);
  const classes = useMemo(
    () => modelDetailQuery.data?.classes.filter((c) => c.isActive) ?? [],
    [modelDetailQuery.data],
  );

  // The whole version map (all models), held as editable local state seeded
  // from the GET read-back. `null` = not yet loaded.
  const [rows, setRows] = useState<AiClassMapping[] | null>(null);
  useEffect(() => {
    if (aiMapQuery.data) setRows(aiMapQuery.data.mappings);
  }, [aiMapQuery.data]);
  const allRows = useMemo(() => rows ?? [], [rows]);

  const [filter, setFilter] = useState<CoverageFilter>("all");
  const [selectedClassUuid, setSelectedClassUuid] = useState<string | null>(
    null,
  );
  const [issues, setIssues] = useState<AiMapValidationIssue[]>([]);

  const typeById = useMemo(
    () => indexEntries(structure.entries.filter((e) => e.axis === "type")),
    [structure.entries],
  );
  const locById = useMemo(
    () => indexEntries(structure.entries.filter((e) => e.axis === "location")),
    [structure.entries],
  );

  // class_uuid → mapping (across the WHOLE version map).
  const mappingByUuid = useMemo(() => {
    const m = new Map<string, AiClassMapping>();
    for (const r of allRows) m.set(r.classUuid, r);
    return m;
  }, [allRows]);

  const tableRows = useMemo(
    () => buildClassRows(classes, allRows, filter),
    [classes, allRows, filter],
  );
  const cov = useMemo(() => coverage(classes, allRows), [classes, allRows]);

  // class_uuid → row index in `allRows` (for offset → issue alignment is by the
  // SAVED order; see savePayload below).
  const selectedClass = useMemo(
    () => classes.find((c) => c.classUuid === selectedClassUuid) ?? null,
    [classes, selectedClassUuid],
  );
  const selectedMapping = selectedClassUuid
    ? mappingByUuid.get(selectedClassUuid)
    : undefined;

  function upsertRow(classUuid: string, patch: Partial<AiClassMapping>): void {
    setRows((prev) => {
      const base = prev ?? [];
      const idx = base.findIndex((r) => r.classUuid === classUuid);
      if (idx === -1) {
        // New row — must carry a typeEntryId to be meaningful; seed empty.
        const created: AiClassMapping = {
          classUuid,
          typeEntryId: "",
          ...patch,
        };
        return [...base, created];
      }
      return base.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    });
  }

  function removeRow(classUuid: string): void {
    setRows((prev) => (prev ?? []).filter((r) => r.classUuid !== classUuid));
  }

  // ── Severity helpers (per class model of the catalogue) ────────────────────
  // The catalogue carries ONE class model (the editor's `classModel`); the
  // override map is keyed by classModelId to mirror the entry shape.
  const classModelId = classModel?.classModelId ?? null;
  const severityClasses = useMemo(
    () =>
      [...(classModel?.classes ?? [])].sort(
        (a, b) => a.classCategory - b.classCategory,
      ),
    [classModel],
  );
  const selectedTypeEntry =
    selectedMapping && selectedMapping.typeEntryId.length > 0
      ? typeById.get(selectedMapping.typeEntryId)
      : undefined;
  const inheritedSev = inheritedSeverity(selectedTypeEntry, classModelId);
  const overrideSev =
    classModelId !== null
      ? (selectedMapping?.severityOverride?.[classModelId] ?? null)
      : null;
  const effectiveSev = overrideSev ?? inheritedSev;

  function setSeverityOverride(defectClassId: string | null): void {
    if (selectedClassUuid === null || classModelId === null) return;
    const cur = selectedMapping?.severityOverride ?? null;
    let next: Record<string, string> | null;
    if (defectClassId === null) {
      // reset → drop this class-model key; null the whole map when empty.
      if (!cur) {
        next = null;
      } else {
        const { [classModelId]: _drop, ...rest } = cur;
        next = Object.keys(rest).length > 0 ? rest : null;
      }
    } else {
      next = { ...(cur ?? {}), [classModelId]: defectClassId };
    }
    upsertRow(selectedClassUuid, { severityOverride: next });
  }

  // ── Note helpers ───────────────────────────────────────────────────────────
  const inheritedNote = selectedTypeEntry?.defaultDescription
    ? locText(selectedTypeEntry.defaultDescription, "")
    : "";
  const noteOverrideText =
    selectedMapping?.noteOverride != null
      ? locText(selectedMapping.noteOverride, "")
      : null;
  const noteIsOverride = selectedMapping?.noteOverride != null;

  function setNoteOverride(text: string): void {
    if (selectedClassUuid === null) return;
    upsertRow(selectedClassUuid, { noteOverride: { en: text } });
  }
  function resetNote(): void {
    if (selectedClassUuid === null) return;
    upsertRow(selectedClassUuid, { noteOverride: null });
  }

  // ── Type / location pickers ────────────────────────────────────────────────
  function pickType(entryId: string): void {
    if (selectedClassUuid === null) return;
    upsertRow(selectedClassUuid, { typeEntryId: entryId });
  }
  function pickLocation(entryId: string): void {
    if (selectedClassUuid === null) return;
    upsertRow(selectedClassUuid, { locationEntryId: entryId });
  }
  function clearLocation(): void {
    if (selectedClassUuid === null) return;
    upsertRow(selectedClassUuid, { locationEntryId: null });
  }

  // Soft-warn: location leaves that are an INVALID pair for the chosen type.
  // The validity overlay resolve gives the OWN ∪ INHERITED valid type set for a
  // location; a location is "invalid for this type" when the type is not in its
  // resolved set. We compute, for the chosen type entry, the set of location
  // leaves whose resolved valid-type set does NOT include it.
  const invalidLocationIds = useMemo(() => {
    const typeId = selectedMapping?.typeEntryId;
    if (typeId == null || typeId.length === 0) return new Set<string>();
    const pairs = structure.validity ?? null;
    const invalid = new Set<string>();
    for (const loc of locById.values()) {
      // only leaves (no children) are selectable.
      const sets = allocationSetsForLocation(
        pairs,
        structure.entries,
        loc.entryId,
      );
      const valid = sets.own.has(typeId) || sets.inherited.has(typeId);
      if (!valid) invalid.add(loc.entryId);
    }
    return invalid;
  }, [
    selectedMapping?.typeEntryId,
    structure.validity,
    structure.entries,
    locById,
  ]);

  // ── Save ────────────────────────────────────────────────────────────────────
  // Only rows with a real typeEntryId are sent (the contract requires it). The
  // 422 issues are 0-based into THIS submitted array, so we keep the same order.
  const savePayload = useMemo(
    () => allRows.filter((r) => r.typeEntryId.length > 0),
    [allRows],
  );
  function handleSave(): void {
    setIssues([]);
    put.mutate(
      { mappings: savePayload },
      {
        onError: (err) => {
          const parsed = extractAiMapIssues(err);
          if (parsed) setIssues(parsed);
        },
      },
    );
  }

  // 422 issues mapped back to the class_uuid of the offending row (via the
  // saved-payload index) so we can flag the table row + selected editor.
  const issueByClassUuid = useMemo(() => {
    const m = new Map<string, AiMapValidationIssue[]>();
    for (const iss of issues) {
      const r = savePayload[iss.row];
      if (!r) continue;
      const arr = m.get(r.classUuid) ?? [];
      arr.push(iss);
      m.set(r.classUuid, arr);
    }
    return m;
  }, [issues, savePayload]);

  const loading =
    modelsQuery.isLoading ||
    aiMapQuery.isLoading ||
    (selectedModelId !== null && modelDetailQuery.isLoading) ||
    rows === null;

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress aria-label="Loading AI map" />
      </Stack>
    );
  }

  if (models.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <Alert severity="info">
          No active AI models. Create a model + class vocabulary under Defects
          &amp; AI › AI Models before building a mapper.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={2}>
        {!editable ? (
          <Alert severity="info">
            This version is read-only. The AI map is frozen on publish and
            copied when you branch a new draft.
          </Alert>
        ) : null}

        {issues.length > 0 ? (
          <Alert severity="error">
            The map was rejected — {String(issues.length)} row
            {issues.length === 1 ? "" : "s"} failed validation. Nothing was
            saved. See the flagged rows below.
          </Alert>
        ) : null}
        {put.isSuccess && issues.length === 0 ? (
          <Alert severity="success">AI map saved.</Alert>
        ) : null}
        {put.isError && issues.length === 0 ? (
          <Alert severity="error">
            Save failed. Check your connection and try again.
          </Alert>
        ) : null}

        {/* Model switcher + coverage row */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          rowGap={1}
        >
          <TextField
            select
            size="small"
            label="Mapper for AI model"
            value={selectedModelId ?? ""}
            onChange={(e) => {
              setSelectedModelId(e.target.value);
              setSelectedClassUuid(null);
            }}
            sx={{ minWidth: 220 }}
            slotProps={{ htmlInput: { "aria-label": "AI model" } }}
          >
            {models.map((m) => (
              <MenuItem key={m.modelId} value={m.modelId}>
                {m.modelName}
              </MenuItem>
            ))}
          </TextField>

          <Typography variant="body2" color="text.secondary">
            Coverage:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {cov.mapped}/{cov.total}
            </Box>{" "}
            classes mapped
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary">
            Σ all models:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {allRows.length}
            </Box>{" "}
            mappings
          </Typography>
        </Stack>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, v: CoverageFilter | null) => {
            if (v) setFilter(v);
          }}
          aria-label="Coverage filter"
        >
          <ToggleButton value="all" aria-label="Show all classes">
            All
          </ToggleButton>
          <ToggleButton value="mapped" aria-label="Show mapped classes">
            Mapped
          </ToggleButton>
          <ToggleButton value="unmapped" aria-label="Show unmapped classes">
            Unmapped
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Class table */}
        <Table size="small" aria-label="AI class mappings">
          <TableHead>
            <TableRow>
              <TableCell>Idx</TableCell>
              <TableCell>AI class</TableCell>
              <TableCell>Defect type (tree path)</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Sev</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map(({ cls, mapping }) => (
              <ClassTableRow
                key={cls.classUuid}
                cls={cls}
                mapping={mapping}
                typeById={typeById}
                locById={locById}
                classModelId={classModelId}
                selected={selectedClassUuid === cls.classUuid}
                hasIssue={issueByClassUuid.has(cls.classUuid)}
                onSelect={() => {
                  setSelectedClassUuid(cls.classUuid);
                }}
              />
            ))}
            {tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No classes match this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>

        <Typography variant="caption" color="text.secondary">
          ⓘ = inherited from entry default · ✎ = per-AI-class override
        </Typography>

        {/* Row editor */}
        {selectedClass ? (
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle2">
                Row editor — {classLabel(selectedClass)}
                {(() => {
                  const dn = classDisplayName(selectedClass);
                  return dn ? ` (${dn})` : "";
                })()}
              </Typography>

              {issueByClassUuid.get(selectedClass.classUuid)?.map((iss, i) => (
                <Alert severity="error" key={i} icon={<TriangleAlertIcon />}>
                  {iss.field}: {iss.message}
                </Alert>
              ))}

              {/* Defect type — required */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Defect type
                </Typography>
                <TreePicker
                  label="Defect type"
                  axis="type"
                  entries={structure.entries}
                  value={
                    selectedMapping?.typeEntryId.length
                      ? selectedMapping.typeEntryId
                      : null
                  }
                  valuePath={entryBreadcrumb(
                    selectedMapping?.typeEntryId,
                    typeById,
                  )}
                  onSelect={pickType}
                  disabled={!editable}
                  placeholder="⚠ pick a defect type"
                />
              </Box>

              {/* Location — optional, clearable */}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Location (optional)
                </Typography>
                <TreePicker
                  label="Location"
                  axis="location"
                  entries={structure.entries}
                  value={selectedMapping?.locationEntryId ?? null}
                  valuePath={entryBreadcrumb(
                    selectedMapping?.locationEntryId,
                    locById,
                  )}
                  onSelect={pickLocation}
                  onClear={clearLocation}
                  disabled={!editable}
                  invalidIds={invalidLocationIds}
                />
                {selectedMapping?.locationEntryId != null &&
                invalidLocationIds.has(selectedMapping.locationEntryId) ? (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <TriangleAlertIcon fontSize="small" color="warning" />
                    <Typography variant="caption" color="warning.main">
                      This location is not a valid pair for the chosen type
                      (validity overlay). Allowed — verify it is intended.
                    </Typography>
                  </Stack>
                ) : null}
              </Box>

              {/* Severity — per class model */}
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Severity
                  </Typography>
                  {classModel ? (
                    <Typography variant="caption" color="text.secondary">
                      ({locText(classModel.modelName, "class model")})
                    </Typography>
                  ) : null}
                </Stack>
                {classModelId === null || severityClasses.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Severity unavailable — the catalogue&apos;s class model has
                    no classes loaded.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      size="small"
                      value={effectiveSev ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSeverityOverride(v.length > 0 ? v : null);
                      }}
                      disabled={!editable}
                      sx={{ minWidth: 200 }}
                      slotProps={{
                        htmlInput: { "aria-label": "Severity" },
                        select: { displayEmpty: true },
                      }}
                    >
                      <MenuItem value="">(none)</MenuItem>
                      {severityClasses.map((c) => (
                        <MenuItem key={c.defectClassId} value={c.defectClassId}>
                          {c.classCategory}
                          {c.valuation ? ` — ${locText(c.valuation, "")}` : ""}
                        </MenuItem>
                      ))}
                    </TextField>
                    {overrideSev === null ? (
                      <Tooltip title="Inherited from the type entry's default severity">
                        <Chip
                          size="small"
                          icon={<InfoIcon />}
                          label="inherited"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Overrides the entry default for this AI class">
                          <Chip
                            size="small"
                            icon={<PencilIcon />}
                            label="override"
                            color="primary"
                            variant="outlined"
                          />
                        </Tooltip>
                        <Button
                          size="small"
                          startIcon={<RotateCcwIcon />}
                          disabled={!editable}
                          onClick={() => {
                            setSeverityOverride(null);
                          }}
                          aria-label="Reset severity to default"
                        >
                          Reset
                        </Button>
                      </>
                    )}
                  </Stack>
                )}
              </Box>

              {/* Note — inherited default_description / override */}
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Note
                  </Typography>
                  {noteIsOverride ? (
                    <Tooltip title="Overrides the entry default description for this AI class">
                      <Chip
                        size="small"
                        icon={<PencilIcon />}
                        label="override"
                        color="primary"
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Inherited from the type entry's default description">
                      <Chip
                        size="small"
                        icon={<InfoIcon />}
                        label="inherited"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Stack>
                <TextField
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  disabled={!editable}
                  value={
                    noteIsOverride ? (noteOverrideText ?? "") : inheritedNote
                  }
                  placeholder={
                    inheritedNote.length > 0
                      ? inheritedNote
                      : "No default description on this entry."
                  }
                  onChange={(e) => {
                    setNoteOverride(e.target.value);
                  }}
                  slotProps={{
                    htmlInput: { "aria-label": "Note" },
                    input: noteIsOverride
                      ? {}
                      : { sx: { color: "text.secondary" } },
                  }}
                />
                {noteIsOverride ? (
                  <Button
                    size="small"
                    startIcon={<RotateCcwIcon />}
                    disabled={!editable}
                    onClick={resetNote}
                    sx={{ mt: 0.5 }}
                    aria-label="Reset note to entry default description"
                  >
                    Reset to entry default
                  </Button>
                ) : null}
              </Box>

              {/* Remove mapping (clears the row for this class) */}
              {selectedMapping ? (
                <Box>
                  <Button
                    size="small"
                    color="error"
                    disabled={!editable}
                    onClick={() => {
                      removeRow(selectedClass.classUuid);
                    }}
                  >
                    Remove mapping for this class
                  </Button>
                </Box>
              ) : null}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a class above to map it.
          </Typography>
        )}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editable || put.isPending}
          >
            Save AI map
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

interface ClassTableRowProps {
  cls: AiFindingModelClass;
  mapping: AiClassMapping | undefined;
  typeById: ReadonlyMap<string, DefectCatalogueEntryV2>;
  locById: ReadonlyMap<string, DefectCatalogueEntryV2>;
  classModelId: string | null;
  selected: boolean;
  hasIssue: boolean;
  onSelect: () => void;
}

function ClassTableRow({
  cls,
  mapping,
  typeById,
  locById,
  classModelId,
  selected,
  hasIssue,
  onSelect,
}: ClassTableRowProps): ReactElement {
  const typePath = mapping
    ? breadcrumbText(entryBreadcrumb(mapping.typeEntryId, typeById))
    : "";
  const locPath =
    mapping?.locationEntryId != null
      ? breadcrumbText(entryBreadcrumb(mapping.locationEntryId, locById))
      : "";
  const typeEntry =
    mapping && mapping.typeEntryId.length > 0
      ? typeById.get(mapping.typeEntryId)
      : undefined;
  const sevOverride =
    classModelId !== null
      ? (mapping?.severityOverride?.[classModelId] ?? null)
      : null;
  const sevInherited = inheritedSeverity(typeEntry, classModelId);
  const sevValue = sevOverride ?? sevInherited;
  const noteOverride = mapping?.noteOverride != null;

  return (
    <TableRow
      hover
      selected={selected}
      onClick={onSelect}
      sx={{
        cursor: "pointer",
        ...(hasIssue ? { bgcolor: "error.light" } : {}),
      }}
      aria-label={`Class ${classLabel(cls)}`}
    >
      <TableCell>{cls.classIndex}</TableCell>
      <TableCell>
        <Stack>
          <Typography variant="body2">{cls.className}</Typography>
          {classDisplayName(cls) ? (
            <Typography variant="caption" color="text.secondary">
              {classDisplayName(cls)}
            </Typography>
          ) : null}
        </Stack>
      </TableCell>
      <TableCell>
        {mapping && mapping.typeEntryId.length > 0 ? (
          <Typography variant="body2">{typePath}</Typography>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TriangleAlertIcon fontSize="small" color="disabled" />
            <Typography variant="body2" color="text.secondary">
              unmapped
            </Typography>
          </Stack>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {locPath.length > 0 ? locPath : "—"}
        </Typography>
      </TableCell>
      <TableCell>
        {sevValue != null ? (
          <Tooltip title={sevOverride != null ? "override" : "inherited"}>
            <span>{sevOverride != null ? "✎" : "ⓘ"}</span>
          </Tooltip>
        ) : (
          "—"
        )}
      </TableCell>
      <TableCell>{noteOverride ? "✎" : mapping ? "ⓘ" : "—"}</TableCell>
    </TableRow>
  );
}
